import { useEffect } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmDialogProps) {
  const { t } = useLanguage();

  // Default labels from translations
  const defaultConfirmLabel = t('common.confirm') || 'Confirm';
  const defaultCancelLabel = t('common.cancel') || 'Cancel';

  // Close on ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="confirm-dialog-overlay" onClick={onCancel}>
      <div
        className={`confirm-dialog-content confirm-dialog-${variant}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="confirm-dialog-header">
          <h2 className="confirm-dialog-title">{title}</h2>
          <button
            onClick={onCancel}
            className="confirm-dialog-close-btn"
            aria-label="Close"
            disabled={isLoading}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Message */}
        <div className="confirm-dialog-message">{message}</div>

        {/* Actions */}
        <div className="confirm-dialog-actions">
          <button
            onClick={onCancel}
            className="confirm-dialog-btn confirm-dialog-btn-cancel"
            disabled={isLoading}
          >
            {cancelLabel || defaultCancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`confirm-dialog-btn confirm-dialog-btn-confirm confirm-dialog-btn-${variant}`}
            disabled={isLoading}
          >
            {isLoading ? t('common.loading') : (confirmLabel || defaultConfirmLabel)}
          </button>
        </div>
      </div>
    </div>
  );
}
